/**
 * VehicleEventsDialog — Schäden / Wartung / Reparatur / Sonstiges für ein Fahrzeug
 * verwalten. Inkl. Foto-Upload und Status-Workflow.
 */
import React, { useEffect, useState, useCallback, useRef } from 'react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../../components/ui/dialog';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Skeleton } from '../../components/ui/skeleton';
import { Badge } from '../../components/ui/badge';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../components/ui/select';
import {
  Wrench, AlertTriangle, FileText, ImageIcon, Plus, Trash2, Upload, Loader2, X,
} from 'lucide-react';
import { toast } from 'sonner';
import { vehiclesAPI } from '../../lib/api';
import { DatePickerDE } from '../../components/ui/date-picker-de';

const EVENT_META = {
  damage:      { label: 'Schaden',     icon: AlertTriangle, color: 'bg-red-100 text-red-800' },
  maintenance: { label: 'Wartung',     icon: Wrench,        color: 'bg-blue-100 text-blue-800' },
  repair:      { label: 'Reparatur',   icon: Wrench,        color: 'bg-amber-100 text-amber-800' },
  other:       { label: 'Sonstiges',   icon: FileText,      color: 'bg-slate-100 text-slate-800' },
};

const STATUS_META = {
  open:        { label: 'Offen',           color: 'bg-amber-100 text-amber-800' },
  in_progress: { label: 'In Bearbeitung',  color: 'bg-blue-100 text-blue-800' },
  done:        { label: 'Erledigt',        color: 'bg-emerald-100 text-emerald-800' },
};

const emptyForm = {
  event_type: 'damage',
  title: '',
  description: '',
  incident_date: '',
  cost: '',
  odometer_km: '',
  workshop: '',
  status: 'open',
  photo_ids: [],
};

const PhotoThumbs = ({ photos, onRemove }) => {
  if (!photos?.length) return null;
  return (
    <div className="flex flex-wrap gap-2 mt-2">
      {photos.map((p) => (
        <div key={p.id} className="relative group">
          <img
            src={vehiclesAPI.photoUrl(p.id)}
            alt={p.original_filename || 'Foto'}
            className="w-20 h-20 object-cover rounded-lg border"
            onError={(e) => { e.target.style.opacity = '0.3'; }}
          />
          {onRemove && (
            <button
              type="button"
              onClick={() => onRemove(p.id)}
              className="absolute -top-1 -right-1 bg-destructive text-white rounded-full p-0.5 opacity-100 md:opacity-0 md:group-hover:opacity-100 transition-opacity"
              data-testid={`remove-photo-${p.id}`}
            >
              <X className="h-3 w-3" />
            </button>
          )}
        </div>
      ))}
    </div>
  );
};

const EventForm = ({ vehicle, formData, setFormData, photos, setPhotos, uploading, onUpload }) => {
  const fileRef = useRef(null);
  const setField = (k, v) => setFormData((p) => ({ ...p, [k]: v }));

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-3">
        <div className="space-y-2">
          <Label>Typ *</Label>
          <Select value={formData.event_type} onValueChange={(v) => setField('event_type', v)}>
            <SelectTrigger data-testid="event-type-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              {Object.entries(EVENT_META).map(([k, m]) => (
                <SelectItem key={k} value={k}>{m.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-2">
          <Label>Status</Label>
          <Select value={formData.status} onValueChange={(v) => setField('status', v)}>
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent>
              {Object.entries(STATUS_META).map(([k, m]) => (
                <SelectItem key={k} value={k}>{m.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="space-y-2">
        <Label>Titel *</Label>
        <Input
          value={formData.title}
          onChange={(e) => setField('title', e.target.value)}
          placeholder="Kurzbeschreibung (z.B. Steinschlag Frontscheibe)"
          data-testid="event-title-input"
        />
      </div>

      <div className="space-y-2">
        <Label>Beschreibung</Label>
        <Textarea
          value={formData.description}
          onChange={(e) => setField('description', e.target.value)}
          placeholder="Details, Hergang, Anweisungen..."
          rows={3}
        />
      </div>

      <div className="grid grid-cols-3 gap-3">
        <div className="space-y-2">
          <Label>Datum</Label>
          <DatePickerDE
            value={formData.incident_date}
            onChange={(v) => setField('incident_date', v)}
            placeholder="Datum wählen"
            testId="event-incident-date"
          />
        </div>
        <div className="space-y-2">
          <Label>Kosten (€)</Label>
          <Input type="number" step="0.01" value={formData.cost}
            onChange={(e) => setField('cost', e.target.value)} />
        </div>
        <div className="space-y-2">
          <Label>KM-Stand</Label>
          <Input type="number" value={formData.odometer_km}
            onChange={(e) => setField('odometer_km', e.target.value)} />
        </div>
      </div>

      <div className="space-y-2">
        <Label>Werkstatt</Label>
        <Input value={formData.workshop}
          onChange={(e) => setField('workshop', e.target.value)}
          placeholder="z.B. Autohaus Müller GmbH" />
      </div>

      <div className="space-y-2">
        <Label className="flex items-center gap-2">
          <ImageIcon className="h-4 w-4" /> Fotos
        </Label>
        <PhotoThumbs photos={photos} onRemove={(id) => setPhotos((prev) => prev.filter((p) => p.id !== id))} />
        <input
          ref={fileRef}
          type="file"
          accept="image/jpeg,image/png,image/webp,image/gif"
          multiple
          className="hidden"
          onChange={(e) => onUpload(Array.from(e.target.files || []))}
          data-testid="event-photo-input"
        />
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={() => fileRef.current?.click()}
          disabled={uploading || !vehicle?.id}
          data-testid="event-photo-upload-btn"
        >
          {uploading
            ? <><Loader2 className="h-3.5 w-3.5 mr-2 animate-spin" />Lade hoch...</>
            : <><Upload className="h-3.5 w-3.5 mr-2" />Foto hinzufügen</>}
        </Button>
        <p className="text-xs text-muted-foreground">JPG, PNG, WEBP, GIF • Max. 8 MB pro Foto</p>
      </div>
    </div>
  );
};

const EventList = ({ events, onEdit, onDelete, canEdit }) => {
  if (!events.length) {
    return (
      <div className="text-center py-10 text-muted-foreground">
        <FileText className="h-10 w-10 mx-auto mb-2 opacity-40" />
        <p className="text-sm">Noch keine Einträge.</p>
      </div>
    );
  }
  return (
    <div className="space-y-2 max-h-[420px] overflow-y-auto pr-1">
      {events.map((e) => {
        const meta = EVENT_META[e.event_type] || EVENT_META.other;
        const statusMeta = STATUS_META[e.status] || STATUS_META.open;
        const Icon = meta.icon;
        return (
          <div key={e.id} className="border rounded-lg p-3 hover:bg-muted/40 transition-colors"
            data-testid={`event-row-${e.id}`}>
            <div className="flex items-start justify-between gap-3">
              <div className="flex-1 min-w-0">
                <div className="flex flex-wrap items-center gap-2 mb-1">
                  <Badge className={meta.color}>
                    <Icon className="h-3 w-3 mr-1" /> {meta.label}
                  </Badge>
                  <Badge className={statusMeta.color}>{statusMeta.label}</Badge>
                  {e.incident_date && (
                    <span className="text-xs text-muted-foreground">
                      {new Date(e.incident_date).toLocaleDateString('de-DE')}
                    </span>
                  )}
                </div>
                <p className="font-medium text-sm">{e.title}</p>
                {e.description && (
                  <p className="text-xs text-muted-foreground mt-1 line-clamp-2">{e.description}</p>
                )}
                <div className="flex flex-wrap gap-3 mt-2 text-xs text-muted-foreground">
                  {e.cost != null && <span>Kosten: {Number(e.cost).toFixed(2)} €</span>}
                  {e.odometer_km != null && <span>KM: {e.odometer_km}</span>}
                  {e.workshop && <span>Werkstatt: {e.workshop}</span>}
                </div>
                <PhotoThumbs photos={e.photos} />
              </div>
              {canEdit && (
                <div className="flex gap-1 shrink-0">
                  <Button size="sm" variant="ghost" onClick={() => onEdit(e)}
                    data-testid={`edit-event-${e.id}`}>Bearbeiten</Button>
                  <Button size="icon" variant="ghost" onClick={() => onDelete(e)}
                    data-testid={`delete-event-${e.id}`}>
                    <Trash2 className="h-4 w-4 text-destructive" />
                  </Button>
                </div>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
};

export const VehicleEventsDialog = ({ open, onOpenChange, vehicle, canEdit }) => {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState(emptyForm);
  const [photos, setPhotos] = useState([]);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);

  const load = useCallback(async () => {
    if (!vehicle?.id) return;
    setLoading(true);
    try {
      const r = await vehiclesAPI.listEvents(vehicle.id);
      setEvents(r.data);
    } catch {
      toast.error('Einträge konnten nicht geladen werden');
    } finally {
      setLoading(false);
    }
  }, [vehicle?.id]);

  useEffect(() => {
    if (open) {
      load();
      setShowForm(false);
      setEditingId(null);
      setFormData(emptyForm);
      setPhotos([]);
    }
  }, [open, load]);

  const handleUpload = async (files) => {
    if (!vehicle?.id || files.length === 0) return;
    setUploading(true);
    try {
      const uploaded = [];
      for (const file of files) {
        if (file.size > 8 * 1024 * 1024) {
          toast.error(`${file.name}: Datei zu groß (max. 8 MB)`);
          continue;
        }
        const r = await vehiclesAPI.uploadPhoto(vehicle.id, file);
        uploaded.push(r.data);
      }
      setPhotos((prev) => [...prev, ...uploaded]);
      if (uploaded.length > 0) toast.success(`${uploaded.length} Foto(s) hochgeladen`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Foto-Upload fehlgeschlagen');
    } finally {
      setUploading(false);
    }
  };

  const startCreate = () => {
    setEditingId(null);
    setFormData(emptyForm);
    setPhotos([]);
    setShowForm(true);
  };

  const startEdit = (event) => {
    setEditingId(event.id);
    setFormData({
      event_type: event.event_type,
      title: event.title,
      description: event.description || '',
      incident_date: event.incident_date || '',
      cost: event.cost ?? '',
      odometer_km: event.odometer_km ?? '',
      workshop: event.workshop || '',
      status: event.status,
      photo_ids: (event.photos || []).map((p) => p.id),
    });
    setPhotos(event.photos || []);
    setShowForm(true);
  };

  const handleSave = async () => {
    if (!formData.title.trim()) {
      toast.error('Titel ist Pflichtfeld');
      return;
    }
    setSaving(true);
    try {
      const payload = {
        ...formData,
        title: formData.title.trim(),
        cost: formData.cost === '' ? null : Number(formData.cost),
        odometer_km: formData.odometer_km === '' ? null : Number(formData.odometer_km),
        photo_ids: photos.map((p) => p.id),
      };
      if (editingId) {
        await vehiclesAPI.updateEvent(vehicle.id, editingId, payload);
        toast.success('Eintrag aktualisiert');
      } else {
        await vehiclesAPI.createEvent(vehicle.id, payload);
        toast.success('Eintrag angelegt');
      }
      setShowForm(false);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Speichern fehlgeschlagen');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (event) => {
    if (!window.confirm(`Eintrag "${event.title}" wirklich löschen?`)) return;
    try {
      await vehiclesAPI.deleteEvent(vehicle.id, event.id);
      toast.success('Eintrag gelöscht');
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Löschen fehlgeschlagen');
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Wrench className="h-5 w-5 text-primary" />
            Schäden &amp; Wartung – {vehicle?.license_plate}
          </DialogTitle>
        </DialogHeader>

        {!showForm && (
          <>
            {canEdit && (
              <div className="flex justify-end mb-3">
                <Button onClick={startCreate} data-testid="new-event-btn">
                  <Plus className="h-4 w-4 mr-2" />
                  Neuer Eintrag
                </Button>
              </div>
            )}
            {loading ? (
              <div className="space-y-2">
                <Skeleton className="h-16 w-full" />
                <Skeleton className="h-16 w-full" />
              </div>
            ) : (
              <EventList events={events} onEdit={startEdit} onDelete={handleDelete} canEdit={canEdit} />
            )}
          </>
        )}

        {showForm && (
          <>
            <EventForm
              vehicle={vehicle}
              formData={formData}
              setFormData={setFormData}
              photos={photos}
              setPhotos={setPhotos}
              uploading={uploading}
              onUpload={handleUpload}
            />
            <DialogFooter className="mt-4">
              <Button variant="outline" onClick={() => setShowForm(false)}>Abbrechen</Button>
              <Button onClick={handleSave} disabled={saving} data-testid="save-event-btn">
                {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                {editingId ? 'Aktualisieren' : 'Anlegen'}
              </Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};
